import { useEffect, useState, useCallback, useRef } from 'react';
import { Typography, Box, CircularProgress, Alert, Divider, Stack, Chip } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import apiClient from '../../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';

import DateRangeSelector from '../../../components/DateRangeSelector';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import PointhomeProfitLossDashboard, {
  CONTENT_CONFIGS,
} from '../../../components/pointhome/contents/profit-loss/PointhomeProfitLossDashboard';

import type { PointhomeProfitLossData } from '../../../types/pointhomeProfitLoss';

export default function PointhomeProfitLoss() {
  const [profitLossData, setProfitLossData] = useState<PointhomeProfitLossData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [initialized, setInitialized] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [activeSubTab, setActiveSubTab] = useState<number>(0);
  const latestRequestId = useRef<number>(0);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...response.data]);
      })
      .catch(() => {
        // 옵션 로드 실패 시에도 화면은 동작하도록 침묵 처리
      });
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchProfitLossData = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);
    // 탭을 빠르게 전환했을 때 늦게 도착한 이전 탭 응답이 현재 탭 데이터를 덮어쓰지 않도록 마지막 요청만 반영한다.
    const requestId = ++latestRequestId.current;

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      content_type: (CONTENT_CONFIGS[activeSubTab] ?? CONTENT_CONFIGS[0]).key,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } : {}),
      ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
    };

    apiClient
      .get(`/api/v1/stat/pointhome/profit-loss`, { params })
      .then((response) => {
        if (requestId !== latestRequestId.current) return;
        setProfitLossData(response.data);
        setErrorMessage('');
      })
      .catch(() => {
        if (requestId !== latestRequestId.current) return;
        setProfitLossData(null);
        setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => {
        if (requestId === latestRequestId.current) setLoading(false);
      });
  }, [startDate, endDate, selectedApp, selectedOs, activeSubTab]);

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized && startDate && endDate && selectedApp != null) {
      fetchProfitLossData();
    }
  }, [initialized, startDate, endDate, selectedApp, selectedOs, fetchProfitLossData]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh',
      }}
    >
      {/* 상단 필터 영역 */}
      <Box alignItems={'center'} display={'flex'}>
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
              if (startDate && endDate) {
                fetchProfitLossData();
              }
            }}
          />
        </Box>
        <Box
          sx={{
            ml: 3,
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(setApp) => {
              setSelectedApp(setApp);
            }}
            onSearch={fetchProfitLossData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading && !profitLossData ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : profitLossData && startDate && endDate ? (
          <PointhomeProfitLossDashboard
            data={profitLossData}
            activeSubTab={activeSubTab}
            onSubTabChange={setActiveSubTab}
            selectedApp={selectedApp}
            startDate={startDate}
            endDate={endDate}
          />
        ) : null}
      </Box>
    </Box>
  );
}
